/**
 * Soft (advisory) field checks — the non-blocking companion to
 * `person-validators.ts`.
 *
 * Unlike the canonical validators, these never invalidate a form. They look at
 * a value and, when it looks suspicious, return an i18n message key (+ optional
 * interpolation params) so the UI can show a gentle, non-blocking warning that
 * helps the user catch a typo: a wrong email domain, an implausible phone
 * number, an IBAN that fails its checksum, a malformed VAT number.
 *
 * Rules of the game:
 *  - Empty values ALWAYS pass (requiredness is a separate, blocking concern).
 *  - A `null` result means "looks fine, stay quiet".
 *  - These are heuristics: they aim to catch obvious mistakes without ever
 *    blocking a save, so they err on the side of silence when unsure.
 */

export type SoftCheckKind = 'email' | 'phone' | 'iban' | 'vat';

export interface SoftAdvice {
  /** ngx-translate key for the warning text. */
  messageKey: string;
  /** Interpolation params for the message (e.g. a suggested correction). */
  params?: Record<string, string>;
}

const KEY = 'shared.softValidation';

// --- Email -----------------------------------------------------------------

/** `local@domain.tld` shape — stricter than Angular's permissive validator. */
const PLAUSIBLE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Curated, conservative map of common email-domain typos → the correct domain.
 * Keyed on the FULL domain (not a generic `.co` heuristic) so we never nudge a
 * legitimate address (e.g. a real `.co` Colombian domain) by mistake.
 */
const EMAIL_DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'yahho.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'iclould.com': 'icloud.com',
  'live.con': 'live.com',
};

export function checkEmail(value: string | null | undefined): SoftAdvice | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  if (!PLAUSIBLE_EMAIL.test(v)) {
    return { messageKey: `${KEY}.email.invalid` };
  }
  const at = v.lastIndexOf('@');
  const domain = v.slice(at + 1).toLowerCase();
  const fix = EMAIL_DOMAIN_TYPOS[domain];
  if (fix) {
    return { messageKey: `${KEY}.email.didYouMean`, params: { suggestion: `${v.slice(0, at)}@${fix}` } };
  }
  return null;
}

// --- Phone -----------------------------------------------------------------

export function checkPhone(value: string | null | undefined): SoftAdvice | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  // Letters in a phone field almost always mean a mistake.
  if (/[a-z]/i.test(v)) return { messageKey: `${KEY}.phone.invalid` };
  const digits = v.replace(/\D/g, '');
  // E.164 caps national numbers at 15 digits; under 7 cannot be a real number.
  if (digits.length < 7 || digits.length > 15) {
    return { messageKey: `${KEY}.phone.invalid` };
  }
  return null;
}

// --- IBAN ------------------------------------------------------------------

/** Official IBAN lengths per country (EU/EEA + a few common neighbours). */
const IBAN_LENGTHS: Record<string, number> = {
  BE: 16, FR: 27, NL: 18, DE: 22, LU: 20, ES: 24, IT: 27, PT: 25, GB: 22,
  CH: 21, AT: 20, IE: 22, PL: 28, DK: 18, SE: 24, FI: 18, NO: 15,
};

/** mod-97 over a long numeric string, processed in safe-integer chunks. */
function mod97(input: string): number {
  let remainder = 0;
  for (let i = 0; i < input.length; i += 7) {
    remainder = Number(`${remainder}${input.slice(i, i + 7)}`) % 97;
  }
  return remainder;
}

export function checkIban(value: string | null | undefined): SoftAdvice | null {
  const raw = (value ?? '').replace(/\s+/g, '').toUpperCase();
  if (!raw) return null;
  const invalid: SoftAdvice = { messageKey: `${KEY}.iban.invalid` };
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(raw)) return invalid;

  const expected = IBAN_LENGTHS[raw.slice(0, 2)];
  if (expected && raw.length !== expected) return invalid;

  // ISO 13616: move the first 4 chars to the end, map letters A→10 … Z→35,
  // then the whole number must be ≡ 1 (mod 97).
  const rearranged = raw.slice(4) + raw.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  return mod97(numeric) === 1 ? null : invalid;
}

// --- VAT -------------------------------------------------------------------

export function checkVat(value: string | null | undefined): SoftAdvice | null {
  const raw = (value ?? '').replace(/[\s.]/g, '').toUpperCase();
  if (!raw) return null;
  const invalid: SoftAdvice = { messageKey: `${KEY}.vat.invalid` };
  // 2-letter country prefix followed by the national number.
  if (!/^[A-Z]{2}[A-Z0-9]{2,12}$/.test(raw)) return invalid;

  // Belgium is the launch market — verify its mod-97 check digits.
  if (raw.startsWith('BE')) {
    let digits = raw.slice(2);
    if (digits.length === 9) digits = `0${digits}`; // legacy 9-digit numbers
    if (!/^\d{10}$/.test(digits)) return invalid;
    const base = Number(digits.slice(0, 8));
    const check = Number(digits.slice(8));
    if (97 - (base % 97) !== check) return invalid;
  }
  return null;
}

// --- Dispatcher ------------------------------------------------------------

/** Run the soft check matching `kind`. Returns `null` when the value looks ok. */
export function softCheck(kind: SoftCheckKind, value: string | null | undefined): SoftAdvice | null {
  switch (kind) {
    case 'email': return checkEmail(value);
    case 'phone': return checkPhone(value);
    case 'iban': return checkIban(value);
    case 'vat': return checkVat(value);
  }
}
